import * as CSS from 'csstype'

import React, {CSSProperties, useMemo} from 'react'
import {defineStyles, useThemedStyles} from './styles/style'

export function Pill({
  color,
  children,
  noPadding
}: {
  color?: CSS.Property.Color
  children?: React.ReactNode
  noPadding?: boolean
}) {
  const s = useThemedStyles(styles)
  const style = useMemo(() => s.pill(color, noPadding), [color, noPadding, s])
  const innerStyle = useMemo(() => s.pillInner(color), [color, s])

  return (
    <div style={style}>
      <div style={innerStyle}></div>
      <>{children}</>
    </div>
  )
}

interface ProgressPillProps {
  pct: number
  pillWidth: number
  color?: CSS.Property.Color
}

export function ProgressPill({color, pct, pillWidth}: ProgressPillProps) {
  const s = useThemedStyles(styles)

  const outerStyle: CSSProperties = useMemo(
    () => ({
      position: 'relative',
      width: `${pillWidth}px`,
      maxWidth: `${pillWidth}px`,
      height: '12px'
    }),
    [pillWidth]
  )

  const progressStyle: CSSProperties = useMemo(() => s.progressBar(pillWidth, pct, color), [pillWidth, pct, color, s])

  return (
    <Pill color={color} noPadding={true}>
      <div style={outerStyle}>
        <div style={progressStyle} />
      </div>
    </Pill>
  )
}

const styles = defineStyles((theme) => ({
  pill: (color?: CSS.Property.Color, noPadding?: boolean) => ({
    display: 'inline-block',
    position: 'relative',
    border: `2px ${color ?? theme.accent.base} solid`,
    color: color ?? theme.accent.base,
    padding: noPadding ? 0 : '2px 4px',
    borderRadius: '100px',
    fontWeight: 'bold',
    fontSize: '12px',
    marginRight: '8px',
    overflow: 'hidden'
  }),
  pillInner: (color?: CSS.Property.Color) => ({
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: color ?? theme.accent.base,
    opacity: 0.1,
    top: 0,
    left: 0
  }),
  progressBar: (pillWidth: number, pct: number, color?: CSS.Property.Color) => ({
    position: 'absolute',
    backgroundColor: color ?? theme.accent.base,
    height: '100%',
    width: `${pillWidth * pct}px`
  })
}))
